import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface RenameDialogDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newTitle: string;
  onNewTitleChange: (title: string) => void;
  onRename: () => void;
}

export function RenameDialogDrawer({
  open,
  onOpenChange,
  newTitle,
  onNewTitleChange,
  onRename,
}: RenameDialogDrawerProps) {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onRename();
    } else if (e.key === 'Escape') {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-[2px] border-synergy-neutral-700 bg-synergy-background-1 sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-synergy-neutral-100">
            {t('history.renameTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('history.renameDesc')}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input
            ref={inputRef}
            value={newTitle}
            onChange={(e) => onNewTitleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="border-2 border-synergy-background-2 bg-synergy-background-2 pl-6 text-base shadow-[0_0_0px_rgba(0,166,255,0)] ring-0 transition-[border-color,box-shadow] duration-300 ease-in-out hover:border-synergy-background-2 hover:shadow-[0_0_4px_rgba(0,166,255,0.9),0_0_5px_rgba(0,166,255,0.7)] focus:border-synergy-blue focus:outline-none"
            placeholder={t('history.renamePlaceholder')}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-synergy-neutral-700 bg-synergy-background-2 text-synergy-neutral-50 [@media(hover:hover)]:hover:bg-synergy-neutral-950 [@media(hover:hover)]:hover:text-synergy-neutral-50"
          >
            {t('dialog.cancel')}
          </Button>
          <Button
            onClick={onRename}
            className="border bg-synergy-neutral-50 text-black [@media(hover:hover)]:hover:border-synergy-neutral-50 [@media(hover:hover)]:hover:bg-synergy-neutral-950 [@media(hover:hover)]:hover:text-synergy-neutral-50"
          >
            {t('dialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
